import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiHelper } from "../../../utils/apiHelper";
import { toast } from "react-toastify";

export const createTeamMember = createAsyncThunk(
    "team/createTeamMember",
    async (memberData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.post("/startup/team-member", memberData);
            if (response.data.code === "00000") {
                toast.success('Takım üyesi başarıyla oluşturuldu');
                return response.data;
            } else {
                throw new Error("Team member creation failed");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const fetchTeamMembers = createAsyncThunk(
    "team/fetchTeamMembers",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/team-members`);
            if (response.data.code === "00000") {
                return response.data.orderedTeamMembers;
            } else {
                throw new Error("Failed to fetch team members");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const deleteTeamMember = createAsyncThunk(
    "team/deleteTeamMember",
    async (teamMemberId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.delete('/startup/team-member', {
                data: { teamMemberId }
            });
            if (response.data.code === "00000") {
                return teamMemberId;
            } else {
                throw new Error("Team member deletion failed");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const updateTeamMember = createAsyncThunk(
    "team/updateTeamMember",
    async (memberData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.put("/startup/team-member", memberData);
            if (response.data.code === "00000") {
                return response.data;
            } else {
                throw new Error("Team member update failed");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const updateTeamMembersOrder = createAsyncThunk(
    "team/updateTeamMembersOrder",
    async (teamMembers, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.put("/startup/team-member-order", { teamMembers });
            if (response.data.code === "00000") {
                return response.data;
            } else {
                throw new Error("Team members order update failed");
            }
        } catch (error) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

const initialState = {
    teamMembers: [],  // Boş array olarak başlat
    loading: false,
    error: null
};

const teamSlice = createSlice({
    name: "team",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(createTeamMember.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createTeamMember.fulfilled, (state, action) => {
                state.loading = false;
                state.teamMembers.push(action.payload);
            })
            .addCase(createTeamMember.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchTeamMembers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTeamMembers.fulfilled, (state, action) => {
                state.loading = false;
                state.teamMembers = action.payload;
            })
            .addCase(fetchTeamMembers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.teamMembers = []; // Hata durumunda boş array
            })
            .addCase(deleteTeamMember.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteTeamMember.fulfilled, (state, action) => {
                state.loading = false;
                state.teamMembers = state.teamMembers.filter(
                    member => member.teamMemberId !== action.payload
                );
            })
            .addCase(deleteTeamMember.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateTeamMember.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTeamMember.fulfilled, (state, action) => {
                state.loading = false;
                // Update the member in the store
                const updatedMember = action.payload.teamMember;
                const index = state.teamMembers.findIndex(m => m.teamMemberId === updatedMember.teamMemberId);
                if (index !== -1) {
                    state.teamMembers[index] = updatedMember;
                }
                // Sort members by order
                state.teamMembers.sort((a, b) => a.order - b.order);
            })
            .addCase(updateTeamMember.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateTeamMembersOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTeamMembersOrder.fulfilled, (state, action) => {
                state.loading = false;
                // Backend'den yeni sıralanmış listeyi al
                if (action.payload.orderedTeamMembers) {
                    state.teamMembers = action.payload.orderedTeamMembers;
                }
            })
            .addCase(updateTeamMembersOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export default teamSlice.reducer;